import clsx from "clsx";
import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  /** Full width button */
  fullWidth?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Icon to display before text */
  iconLeft?: React.ReactNode;
  /** Icon to display after text */
  iconRight?: React.ReactNode;
  children: React.ReactNode;
}

const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: clsx(
    "bg-[var(--interactive-primary)] text-[var(--text-inverse)]",
    "hover:bg-[var(--interactive-primary-hover)]",
    "focus:ring-[var(--interactive-primary)]",
  ),
  secondary: clsx(
    "bg-[var(--surface-card)] text-[var(--text-primary)]",
    "border border-[var(--border-default)]",
    "hover:bg-[var(--interactive-secondary-hover)]",
    "focus:ring-[var(--interactive-primary)]",
  ),
  outline: clsx(
    "bg-transparent text-[var(--text-primary)]",
    "border border-[var(--border-default)]",
    "hover:bg-[var(--interactive-secondary)]",
    "focus:ring-[var(--interactive-primary)]",
  ),
  danger: clsx(
    "bg-[var(--danger-500)] text-[var(--text-inverse)]",
    "hover:bg-[var(--danger-600)]",
    "focus:ring-[var(--danger-500)]",
  ),
  ghost: clsx(
    "bg-transparent text-[var(--interactive-primary)]",
    "hover:bg-[var(--brand-100)]",
    "focus:ring-[var(--interactive-primary)]",
  ),
};

const sizeClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
  sm: "px-3 py-1.5 text-[13px] gap-1.5",
  md: "px-4 py-2 text-[14px] gap-2",
  lg: "px-6 py-3 text-[15px] gap-2.5",
};

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  iconLeft,
  iconRight,
  children,
  className,
  disabled,
  ...props
}) => {
  return (
    <button
      className={clsx(
        // Base styles
        "inline-flex items-center justify-center",
        "font-medium",
        "rounded-[var(--radius-lg)]",
        "transition-all duration-[var(--duration-normal)]",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        // Variant styles
        variantClasses[variant],
        // Size styles
        sizeClasses[size],
        // Full width
        fullWidth && "w-full",
        // Disabled/Loading state
        (disabled || loading) && "opacity-50 cursor-not-allowed",
        // Custom classes
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <LoadingSpinner />
          <span>Loading...</span>
        </>
      ) : (
        <>
          {iconLeft && <span className="flex-shrink-0">{iconLeft}</span>}
          {children}
          {iconRight && <span className="flex-shrink-0">{iconRight}</span>}
        </>
      )}
    </button>
  );
};

const LoadingSpinner: React.FC = () => (
  <svg
    className="animate-spin h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default Button;
